import {inject, Injectable} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {App, RestoredListenerEvent} from '@capacitor/app';
import {Photo} from '@capacitor/camera';
import {PhotoService} from './photo.service';
import {EventDraftService} from '../event/event-draft.service';
import {PostHogService} from '../posthog/posthog.service';
import {LoggerService} from '../logging/logger.service';
import {AssociationType} from '../../models/association-type';
import {EVENT_DRAFT_ID, EventDraft} from '../../models/event-draft.model';
import {
  FIRST_ACTIVITY_DRAFT_ID,
  FirstActivityDraft
} from '../../models/first-activity-draft.model';
import {
  calculateBinarySize,
  fetchWebPathAsBase64,
  isCancellationMessage,
  MAX_FILE_BYTES,
  MAX_PHOTOS
} from '../../utils/photo-data.util';

/** Either HEC-178 draft a restored photo can be attached to. */
type RecoverableDraft = EventDraft | FirstActivityDraft;

type Outcome =
  'photo_capture_received' | 'photo_capture_cancelled' | 'photo_capture_error';

/**
 * Recovers a camera photo whose capture outlived the app process
 * (HEC-180). When Android kills the WebView behind the camera the
 * Camera.getPhoto() promise dies with it; on the next cold start
 * Capacitor re-delivers the result as the retained `appRestoredResult`
 * event. initialise() records that result during bootstrap, and
 * settle(), awaited by the two start-up draft consumers (the `tabs`
 * resume guard and FirstActivityPage) before they read their draft,
 * saves the photo through PhotoService and appends it to the stored
 * draft so HEC-178's resume shows it.
 *
 * Deliberately does not inject ErrorService: nothing on this path may
 * show a toast (AC Scenario 3). Failures are logged and counted in
 * PostHog only.
 */
@Injectable({providedIn: 'root'})
export class PhotoRecoveryService {
  /** Bound on the bridge round trip awaited inside the app initialiser. */
  static readonly INITIALISE_TIMEOUT_MS = 2500;
  /** Bound on the recovery a draft consumer waits for before resuming. */
  static readonly SETTLE_TIMEOUT_MS = 5000;

  private readonly photoService = inject(PhotoService);
  private readonly draftService = inject(EventDraftService);
  private readonly postHogService = inject(PostHogService);
  private readonly logger = inject(LoggerService);

  private pending: RestoredListenerEvent | null = null;
  // The one bounded wait every settle() caller shares; set by the first
  // call that finds a pending result.
  private settlement: Promise<void> | null = null;
  private settled = false;

  /**
   * Registers the restored-result listener on Android; a no-op on web
   * and iOS. Awaited by bootstrap through main.ts and bounded by
   * INITIALISE_TIMEOUT_MS, so a stalled bridge can never hold the app.
   * No storage or filesystem work happens here: the per-user Draft store
   * is not resolvable until the user context exists.
   */
  async initialise(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }
    await this.bounded(
      this.registerListener(),
      PhotoRecoveryService.INITIALISE_TIMEOUT_MS,
      'Restored-result listener not confirmed in time; continuing bootstrap'
    );
  }

  /**
   * Attaches a pending restored photo to the stored draft, then
   * resolves. Resolves at once when nothing is pending, shares one
   * recovery run and one bounded wait between repeated callers, never
   * rejects, and is bounded by SETTLE_TIMEOUT_MS. The `tabs` guard calls
   * this on every child navigation, so once that wait has resolved, by
   * completion or by the bound, every later call resolves at once rather
   * than racing the same recovery for another SETTLE_TIMEOUT_MS. A
   * recovery that outlives the bound carries on in the background: the
   * file lands on disk but the resumed form's next snapshot overwrites
   * the amended draft, and the existing photo housekeeping removes the
   * orphan with the event's other local files. That degradation is
   * accepted; a form that never appears is not.
   */
  settle(): Promise<void> {
    this.settled = true;
    if (!this.settlement && this.pending) {
      const recovery = this.recover(this.pending).catch(error => {
        this.logger.error('Photo recovery failed', error);
      });
      this.pending = null;
      this.settlement = this.bounded(
        recovery,
        PhotoRecoveryService.SETTLE_TIMEOUT_MS,
        'Photo recovery still running, resuming without it'
      );
    }
    return this.settlement ?? Promise.resolve();
  }

  /**
   * The getState() round trip after addListener() is a barrier, not a
   * delay. Needed: Plugin.addListener is RETURN_NONE (Plugin.java:730-736),
   * so native never answers it, and core's addListenerNative
   * (dist/index.js:141-156) resolves as soon as the message is posted; on
   * its own `await App.addListener(...)` proves nothing. Correct: the
   * Android bridge runs plugin methods in call order on one HandlerThread
   * (Bridge.java:138, :854 taskHandler.post); addListener hands a retained
   * appRestoredResult to the new listener synchronously inside that call
   * (Plugin.java:627-641, 712-724), and notifyListeners ends in
   * call.resolve (Plugin.java:660-682), which goes through the same
   * MessageHandler.sendResponseMessage as every other response; every
   * response leaves that thread in order over the FIFO
   * javaScriptReplyProxy.postMessage (MessageHandler.java:126), with
   * legacySendResponseMessage (:141-145) only the fallback for the legacy
   * bridge. So once getState() has resolved here a retained result has
   * already run record(), and because bootstrap awaits initialise(),
   * `pending` is populated before any guard or page can call settle().
   */
  private async registerListener(): Promise<void> {
    try {
      await App.addListener('appRestoredResult', event => this.record(event));
      await App.getState();
    } catch (error) {
      this.logger.warn('Restored-result listener not registered', error);
    }
  }

  private record(event: RestoredListenerEvent): void {
    if (event.pluginId !== 'Camera' || event.methodName !== 'getPhoto') {
      this.logger.debug(
        'Ignoring restored result', event.pluginId, event.methodName);
      return;
    }
    if (this.settled) {
      // The resume one-shot is spent: the form may already be up and its
      // next snapshot would overwrite an amended draft, so drop it.
      this.logger.warn('Restored camera result arrived after resume; dropped');
      return;
    }
    this.pending = event;
  }

  private async recover(event: RestoredListenerEvent): Promise<void> {
    // RestoredListenerEvent.data is `any`; narrow once to the camera shape.
    const photo = event.data as Photo | undefined;
    if (event.success === false) {
      const message = event.error?.message;
      if (isCancellationMessage(message)) {
        void this.captureOutcome('photo_capture_cancelled', {});
      } else {
        void this.captureOutcome('photo_capture_error', {message});
      }
      return;
    }
    if (!photo?.webPath) {
      // The camera analogue of an empty gallery pick (see
      // PhotoListComponent.takePhoto): it opened and produced nothing.
      void this.captureOutcome('photo_capture_cancelled', {});
      return;
    }
    const draft = await this.findTargetDraft();
    if (!draft) {
      this.logger.warn('Restored photo has no draft to attach to');
      void this.captureOutcome('photo_capture_received', {attached: false});
      return;
    }
    if (draft.photos.length >= MAX_PHOTOS) {
      this.logger.warn('Restored photo dropped: the draft is full');
      void this.captureOutcome('photo_capture_received', {attached: false});
      return;
    }
    const base64String = await fetchWebPathAsBase64(photo.webPath);
    if (!base64String) {
      this.logger.error('Restored photo could not be read', photo.webPath);
      void this.captureOutcome('photo_capture_error', {message: 'read failed'});
      return;
    }
    if (calculateBinarySize(base64String) > MAX_FILE_BYTES) {
      this.logger.warn('Restored photo dropped: over the per-photo size limit');
      void this.captureOutcome('photo_capture_received', {attached: false});
      return;
    }
    await this.attach(draft, base64String, photo.format || 'jpeg');
  }

  private async attach(
    draft: RecoverableDraft, base64String: string, format: string
  ): Promise<void> {
    const saved = await this.photoService.savePicture(
      {base64String, format, saved: false}, AssociationType.EVENT, draft.eventId);
    if (!saved) {
      this.logger.error('Restored photo could not be saved');
      void this.captureOutcome('photo_capture_error', {message: 'save failed'});
      return;
    }
    draft.photos = [...draft.photos, {name: saved.name, path: saved.path}];
    if (draft.id === EVENT_DRAFT_ID) {
      // So the activity form uploads it on save.
      draft.photosHaveChanged = true;
    }
    draft.savedAt = new Date().toISOString();
    // flush() writes every pending draft id, not just this one, which is
    // harmless here: nothing else has scheduled a write this early.
    this.draftService.scheduleSave(draft);
    await this.draftService.flush();
    this.logger.info('Restored photo attached to draft', draft.id, saved.name);
    void this.captureOutcome('photo_capture_received', {attached: true});
  }

  private async findTargetDraft(): Promise<RecoverableDraft | null> {
    const [eventDraft, firstActivityDraft] = await Promise.all([
      this.draftService.load<EventDraft>(EVENT_DRAFT_ID),
      this.draftService.load<FirstActivityDraft>(FIRST_ACTIVITY_DRAFT_ID)
    ]);
    const drafts = [eventDraft, firstActivityDraft]
      .filter((draft): draft is RecoverableDraft => draft !== null);
    if (drafts.length === 0) {
      return null;
    }
    // With both on disk, the page whose camera was open was the one last
    // edited, so its scheduleSave stamped the later savedAt.
    return drafts.reduce((latest, draft) =>
      Date.parse(draft.savedAt) > Date.parse(latest.savedAt) ? draft : latest);
  }

  /**
   * Fire-and-forget, off the settle() critical path: PostHog silently
   * drops captures made before it is initialised, so wait for it here.
   */
  private async captureOutcome(
    name: Outcome, properties: Record<string, unknown>
  ): Promise<void> {
    await this.postHogService.whenReady();
    this.postHogService.capture(
      name, {source: 'camera', restored: true, ...properties});
  }

  /** Resolves when `work` settles or after `ms`, warning on the bound. */
  private bounded(
    work: Promise<void>, ms: number, timeoutMessage: string
  ): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>(resolve => {
      timer = setTimeout(() => {
        this.logger.warn(timeoutMessage);
        resolve();
      }, ms);
    });
    return Promise.race([work.finally(() => clearTimeout(timer)), timeout]);
  }
}
